import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { EditOutlined } from '@ant-design/icons';
import { Button, Card, Row } from 'antd';

import AuditLog from 'components/AuditLog';
import RenderInGrid from 'components/Layout/RenderInGrid';
import { ImageItem } from 'components/Layout/list';
import Label from 'components/Text/Label';
import { ActionsBtnGroup, SectionHeader } from 'containers/BDO/utils';
import { IParamsWithContainId } from 'utils/types';

import AddEditCreditCardModal from './AddEditCreditCardModal';
import { getCreditCard } from './actions';
import { BRAND_LABELS, CARD_TYPES_REQUIRING_BIN, CardType } from './constants';

const CreditCardsShow = () => {
  const dispatch = useDispatch();
  const params = useParams<IParamsWithContainId>();
  const [isEdit, setIsEdit] = useState(false);

  const creditCardId = params.id;

  const { creditCard, loading, error } = useSelector((state: any) => state.creditCards);

  useEffect(() => {
    if (creditCardId) {
      dispatch(getCreditCard(creditCardId));
    }
  }, [creditCardId, dispatch]);

  const renderInGrid = (description, content, hasContentChanged?: boolean) => {
    return (
      <RenderInGrid
        description={description}
        content={content}
        hasContentChanged={hasContentChanged}
      />
    );
  };

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleEditClick = () => {
    setIsEdit(true);
    setIsModalOpen(true);
  };

  const handleDuplicateClick = () => {
    setIsEdit(false);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    dispatch(getCreditCard(creditCardId));
  };

  return (
    <>
      <Card>
        <Row justify="space-between">
          <SectionHeader>Credit Card Details</SectionHeader>
          <ActionsBtnGroup>
            <Button size="large" type="primary" ghost onClick={handleDuplicateClick}>
              Duplicate
            </Button>
            <Button size="large" type="primary" icon={<EditOutlined />} onClick={handleEditClick}>
              Edit
            </Button>
          </ActionsBtnGroup>
        </Row>
        {renderInGrid(
          'Card Name:',
          <div data-testid="card_name">
            <Label dark>{creditCard?.name}</Label>
          </div>
        )}
        {renderInGrid(
          'Card Brand:',
          <div data-testid="card_brand">
            <Label dark>
              {BRAND_LABELS[creditCard?.card_type as CardType] ?? creditCard?.card_type}
            </Label>
          </div>
        )}
        {renderInGrid(
          'Card Image:',
          <ImageItem>
            <img data-testid="card_image" src={creditCard?.image?.url} alt={creditCard?.name} />
          </ImageItem>
        )}
        {renderInGrid(
          'Card Product Code:',
          <div data-testid="card_product_code">
            <Label dark>{creditCard?.product_code}</Label>
          </div>
        )}
        {CARD_TYPES_REQUIRING_BIN.includes(creditCard?.card_type as CardType) && (
          <>
            {renderInGrid(
              'BIN Number:',
              <div data-testid="card_bin_number">
                <Label dark>{creditCard?.bin_number}</Label>
              </div>
            )}
            {renderInGrid(
              'Min Redemption:',
              <div data-testid="card_minimum_points">
                <Label dark>{creditCard?.minimum_points}</Label>
              </div>
            )}
          </>
        )}
      </Card>
      <AuditLog
        entity="bdo_transfer_request_card"
        id={creditCardId}
        isChanges2
        key={creditCard?.id}
      />
      <AddEditCreditCardModal
        modalOpen={isModalOpen}
        closeModal={handleCloseModal}
        isEdit={isEdit}
        selectedCreditCard={creditCard?.id}
      />
    </>
  );
};

export default CreditCardsShow;
